import { createSlice } from '@reduxjs/toolkit';

// Origem de operação — 'pt' (Petrotec Portugal, comportamento actual) vs
// 'ao' (Petrotec Angola, ecrãs novos da Fase A). Deriva 100% do tenant:
// `clientSettings.defaultCountry === 'AO'` → 'ao'; tudo o resto → 'pt'.
// O App.js despacha `applyClientDefaultOrigin` quando o /auth/me chega.

const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    activeView: 'planner', // 'dashboard' | 'planner' | 'fleet' | 'reports'
    previousView: null, // set by navigateTo() so we know where to go "back"
    mapProvider: 'google', // 'mapbox' | 'google'
    operationOrigin: 'pt', // 'pt' | 'ao' — gate das features Petrotec Angola; sobrescrito pelo applyClientDefaultOrigin no login
    // Drilldown da hierarquia Petrotec Angola: FinalCustomer → CustomerSite → SiteEquipment.
    // Guarda {id, name} do cliente final seleccionado para mostrar o cabeçalho contextual
    // em CustomerSitesView. Limpo quando se sai do fluxo.
    selectedFinalCustomer: null, // { id, name } | null
    // Posto seleccionado para drilldown de equipamentos.
    // Inclui finalCustomer{Id,Name} para o breadcrumb não precisar de novo fetch.
    selectedCustomerSite: null, // { id, name, finalCustomerId, finalCustomerName } | null
    activeSidebarTab: 0,
    showDetails: false,
    detailsPanelOpen: false,
    showReports: false,
    showMobilePreview: false,
    lang: 'pt',
    mapClickMode: null, // 'origin' | 'destination' | 'waypoint' | null
    needsRecalculation: false,
    // Piloto automático (OT fuel): calcular + gravar a rota e voltar à cronologia
    // sem o operador tocar no planeador. Activado pelo diálogo "Fim de carregamento".
    autoSaveReturn: false,
    resetMapView: false,
    poiFilters: { services: true, truckParking: true, restArea: true },
    highlightedPoiId: null,
    hoveredStopIdx: null,
    hoveredMarkerId: null,
    hoveredSavedLocation: null, // { lng, lat, name } — temporary pin shown on map when hovering saved location
    highlightedStepIdx: null, // index of route step to highlight on map
    displayedSavedRoutes: [], // array of saved route objects to show on map
    hoveredCompareRouteIdx: null, // index of hovered route in compare dialog
    sidebarVisible: false, // floating sidebar visibility (planner view) — starts hidden, SearchBar shown
    sidebarCollapsed: false, // sidebar collapsed but still "visible" — toggled by edge button
    pendingOrderTimelineId: null, // OT a abrir/expandir (cronologia) ao chegar às Ordens — deep-link da popup do veículo
  },
  reducers: {
    setPendingOrderTimelineId: (state, action) => {
      state.pendingOrderTimelineId = action.payload;
    },
    setActiveView: (state, action) => {
      // Direct navigation (e.g. main menu) — clears back-stack
      state.activeView = action.payload;
      state.previousView = null;
    },
    // Contextual navigation: remembers where we came from so we can show a back button
    navigateTo: (state, action) => {
      const target = action.payload;
      if (target !== state.activeView) {
        state.previousView = state.activeView;
        state.activeView = target;
      }
    },
    clearPreviousView: (state) => {
      state.previousView = null;
    },
    setSidebarTab: (state, action) => {
      state.activeSidebarTab = action.payload;
    },
    setShowDetails: (state, action) => {
      state.showDetails = action.payload;
    },
    setDetailsPanelOpen: (state, action) => {
      state.detailsPanelOpen = action.payload;
    },
    toggleDetailsPanel: (state) => {
      state.detailsPanelOpen = !state.detailsPanelOpen;
    },
    setShowReports: (state, action) => {
      state.showReports = action.payload;
    },
    setShowMobilePreview: (state, action) => {
      state.showMobilePreview = action.payload;
    },
    setLang: (state, action) => {
      state.lang = action.payload;
    },
    setMapClickMode: (state, action) => {
      state.mapClickMode = action.payload;
    },
    setNeedsRecalculation: (state, action) => {
      state.needsRecalculation = action.payload;
    },
    setAutoSaveReturn: (state, action) => {
      state.autoSaveReturn = action.payload;
    },
    setResetMapView: (state, action) => {
      state.resetMapView = action.payload;
    },
    setPoiFilter: (state, action) => {
      state.poiFilters = { ...state.poiFilters, ...action.payload };
    },
    setHighlightedPoiId: (state, action) => {
      state.highlightedPoiId = action.payload;
    },
    setHoveredStopIdx: (state, action) => {
      state.hoveredStopIdx = action.payload;
    },
    setHoveredMarkerId: (state, action) => {
      state.hoveredMarkerId = action.payload;
    },
    setHoveredSavedLocation: (state, action) => {
      state.hoveredSavedLocation = action.payload;
    },
    setDisplayedSavedRoutes: (state, action) => {
      state.displayedSavedRoutes = action.payload;
    },
    setHighlightedStepIdx: (state, action) => {
      state.highlightedStepIdx = action.payload;
    },
    setHoveredCompareRouteIdx: (state, action) => {
      state.hoveredCompareRouteIdx = action.payload;
    },
    setSidebarVisible: (state, action) => {
      state.sidebarVisible = action.payload;
    },
    setMapProvider: (state, action) => {
      state.mapProvider = action.payload;
    },
    setSidebarCollapsed: (state, action) => {
      state.sidebarCollapsed = action.payload;
    },
    // Cliente final seleccionado para drilldown (Postos / Equipamentos).
    // Aceita {id, name} ou null para limpar.
    setSelectedFinalCustomer: (state, action) => {
      state.selectedFinalCustomer = action.payload || null;
    },
    // Posto seleccionado para drilldown de Equipamentos.
    // Aceita {id, name, finalCustomerId, finalCustomerName} ou null.
    setSelectedCustomerSite: (state, action) => {
      state.selectedCustomerSite = action.payload || null;
    },
    // Aplica o default do tenant (clientSettings.defaultCountry).
    // Despachado pelo App.js sempre que o /auth/me ou /auth/login chega.
    // Não há override manual — a fonte de verdade é o tenant.
    applyClientDefaultOrigin: (state, action) => {
      const country = String(action.payload || '').toUpperCase();
      state.operationOrigin = country === 'AO' ? 'ao' : 'pt';
    },
  },
});

export const { setPendingOrderTimelineId, setActiveView, navigateTo, clearPreviousView, setSidebarTab, setShowDetails, setDetailsPanelOpen, toggleDetailsPanel, setShowReports, setShowMobilePreview, setLang, setMapClickMode, setNeedsRecalculation, setAutoSaveReturn, setResetMapView, setPoiFilter, setHighlightedPoiId, setHoveredStopIdx, setHoveredMarkerId, setHoveredSavedLocation, setDisplayedSavedRoutes, setHighlightedStepIdx, setHoveredCompareRouteIdx, setSidebarVisible, setMapProvider, setSidebarCollapsed, applyClientDefaultOrigin, setSelectedFinalCustomer, setSelectedCustomerSite } = uiSlice.actions;
export const selectUI = state => state.ui;
export default uiSlice.reducer;
